import { ElementRef, EventEmitter, Injectable, Output } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject } from "rxjs";
import { SelectionModel } from '@angular/cdk/collections';
import { PageEventType } from 'libComm';
import {
	ActionConfig,
	ColumnConfig,
	ComputedRowState,
	DatasetConfig,
	SortConfig,
	ACTION_IDS
} from '../components/shared/data-table/models/data-table.models';
import { ActionSet, DataSet } from 'src/app/models/layout';
import {SortingUserPrefService} from "./utils/SortingUserPref.service";

// Column width tuning shared by the content-based weight calculation
export const TABLE_MIN_COL_WIDTH = 70;
export const TABLE_MAX_COL_WIDTH = 320;
export const TABLE_MAX_HEADER_WIDTH = 200;
export const TABLE_CANVAS_MARGIN = 28;

@Injectable()
export class TableControllerService {

	dataSet: any[] = [];
	deletedRows: any[] = [];
	tableName :string = '';

	row: any;
	rowNumber: number = -1;

	selection = new SelectionModel<any>(true, []);

	numElementsLoaded: number = 0;
	numElementsSize: number = 0;

	public _filterValues: BehaviorSubject<any> = new BehaviorSubject<any>({})

	public filterValues = this._filterValues.asObservable();

	@Output()
	onPaginationChange: EventEmitter<PageEventType> =
		new EventEmitter<PageEventType>();

	@Output()
	onSortChange: EventEmitter<{
		sorting: Map<string, { order: number; type: 'asc' | 'desc' }>;
		pageEvent: PageEventType;
	}> = new EventEmitter<{
		sorting: Map<string, { order: number; type: 'asc' | 'desc' }>;
		pageEvent: PageEventType;
	}>();

	sortedColumns: Map<string, { order: number; type: 'asc' | 'desc' }> =
		new Map();


	private errors: Map<number, Map<string, boolean>> = new Map<number, Map<string, boolean>>()

	public columnWeight: Record<string, number> = {};

	public columnSize: Record<string, number> = {};

	private tableRef?: ElementRef;
	private viewPortRef?: { elementRef: ElementRef };

	// === REFERENCES (set by component) ===
	private _datasets: DatasetConfig | null = null;
	private _actionset: ActionSet | null = null;
	private _disabledActions: ActionConfig[] = [];


	constructor(public sortService: SortingUserPrefService)
	{
	}

	// ==========================================
	// STATE SETTERS
	// ==========================================

	setDatasets(datasets: DatasetConfig | DataSet | null): void {
		this.tableName = (datasets as DataSet)?.tableName ?? '';
		this._datasets = this.normalizeDatasets(datasets);
	}

	getDatasets(): DatasetConfig | null {
		return this._datasets;
	}

	private normalizeDatasets(datasets: DatasetConfig | DataSet | null): DatasetConfig | null {
		if (!datasets) return null;
		const datas = (datasets as DataSet).datas ?? (datasets as DatasetConfig).datas ?? [];
		return {
			datas: datas as ColumnConfig[],
			multiselect: (datasets as DataSet).multiselect ?? (datasets as DatasetConfig).multiselect
		};
	}

	setActionset(actionset: ActionSet | null): void {
		this._actionset = actionset;
	}

	setDisabledActions(disabledActions: ActionConfig[]): void {
		this._disabledActions = disabledActions;
	}

	setSortedColumns(sortedColumns: Map<string, { order: number; type: 'asc' | 'desc' }>): void {
		this.sortedColumns = sortedColumns;
	}

	// ==========================================
	// STATE GETTERS
	// ==========================================

	getVisibleColumns(): ColumnConfig[] {
		return this._datasets?.datas?.filter(
			col => col.hidden !== true && col.columnPosition >= 0
		) ?? [];
	}

	getHiddenColumns(): ColumnConfig[] {
		return this._datasets?.datas?.filter(col => col.hidden === true) ?? [];
	}

	hasAction(actionId: string): boolean {
		const actions = this._actionset?.actions ?? [];
		return actions.some(a => a.id === actionId);
	}

	getAvailableActionsSet(): Set<string> {
		const actions = this._actionset?.actions ?? [];
		return new Set(actions.map(a => a.id));
	}

	isActionDisabled(actionId: string): boolean {
		return this._disabledActions.some(act => act.id === actionId);
	}

	getSortedColumnInfo(columnName: string): SortConfig | null {
		const sortInfo = this.sortedColumns.get(columnName);
		if (!sortInfo) return null;
		return {
			columnName,
			type: sortInfo.type,
			order: sortInfo.order
		};
	}

	isInEditOrInsertMode(): boolean {
		return this.dataSet.some(row => row?.newRow || row?.editing);
	}

	getRowState(index: number): ComputedRowState {
		const row = this.dataSet[index];
		if (!row) {
			return {
				isEditing: false,
				isCanceled: false
			};
		}

		return {
			isEditing: !!row?.editing || !!row?.newRow,
			isCanceled: !!row?.STATUS && row.STATUS === 'CANCELED'
		};
	}

	isRowCanceled(index: number): boolean {
		const row = this.dataSet[index];
		return !!row?.STATUS && row.STATUS === 'CANCELED';
	}

	showMultiselectDropdown(): boolean {
		if (!this._datasets?.multiselect) return false;
		if (!this._actionset?.actions?.length) return false;
		return this.hasAction(ACTION_IDS.EXPORT) || this.hasAction(ACTION_IDS.DELETE) || this.hasAction("ACT_APPROVE");
	}

	showMultiselectDropdownMenu(): boolean {
		if (!this._actionset?.actions?.length) return false;
		return this.hasAction(ACTION_IDS.EXPORT) || this.hasAction(ACTION_IDS.DELETE) || this.hasAction("ACT_APPROVE");
	}

	showActionsColumn(actionsToCheck: string[]): boolean {
		const actions = this._actionset?.actions ?? [];
		if (actions.length === 0) return false;
		//return actions.some(action => actionsToCheck.includes(action.id));
		return true;
	}

	onAddClick(newRowObj: any) {
		newRowObj.newRow = true;
		newRowObj.editing = true;

		newRowObj = this.customizeAfterAddClick(newRowObj);

		if (!!this.dataSet && this.dataSet.length > 0) {
			this.dataSet = [_.cloneDeep(newRowObj), ...this.dataSet];
			this.row = this.dataSet[this.rowNumber + 1]
		} else {
			this.dataSet = [_.cloneDeep(newRowObj)];
		}

		return this.dataSet;
	}

	customizeAfterAddClick(newRowObj:any){
		return newRowObj;
	}

	editRow(index: number) {
		this.dataSet[index].backup = _.cloneDeep(this.dataSet[index]);
		this.dataSet[index].editing = true;
	}

	exitRow(index: number) {
		if (!!this.dataSet[index].newRow) {
			this.dataSet.splice(index, 1);
		} else {
			this.dataSet[index] = this.dataSet[index].backup;
		}
	}

	commitEdit(index: number) {
		delete this.dataSet[index]['editing'];
	}

	commitEditDetail(index: number) {
		this.rowNumber = index;
		if (!!this.dataSet) this.row = this.dataSet[index];
	}

	deleteRow(index: number) {
		this.rowNumber = index;
		if (this.dataSet[index].newRow == undefined) {
			this.deletedRows.push(this.dataSet[index]);
		}
		return this.dataSet;
	}

	/** Whether the number of selected elements matches the total number of rows. */
	isAllSelected() {
		const numSelected = !!this.selection ? this.selection.selected.length : 0;
		const numRows = !!this.dataSet ? this.dataSet.length : 0;
		return !!this.dataSet ? numSelected === numRows : false;
	}

	/** Selects all rows if they are not all selected; otherwise clear selection. */
	toggleAllRows() {
		if (this.isAllSelected()) {
			this.selection.clear();
			return;
		}

		this.selection.select(...this.dataSet);
	}

	/** The label for the checkbox on the passed row */
	checkboxLabel(row?: any): string {
		if (!row) {
			return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
		}
		return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
			row.position + 1
		}`;
	}

	setErrors($event: boolean, i: number, columnName: any, isDisabled: boolean = false) {
		if(!isDisabled)
			this.errors.get(i)?.set(columnName, $event);
	}

	setTableRefs(tableRef: ElementRef | undefined, viewPort?: { elementRef: ElementRef }): void
	{
		this.tableRef = tableRef;
		this.viewPortRef = viewPort;
	}

	calculateColumnWidth(): void
	{
		if (!this.tableRef) return;

		const trWidth = this.viewPortRef?.elementRef.nativeElement.offsetWidth ||
			this.tableRef.nativeElement.offsetWidth;

		const totalSizeColumn = Object.values(this.columnWeight).reduce((acc, curr) => acc + curr, 1);

		for (const key of Object.keys(this.columnWeight))
		{
			this.columnSize[key] = Math.ceil(
				totalSizeColumn < trWidth
					? (trWidth / totalSizeColumn) * this.columnWeight[key]
					: this.columnWeight[key]
			);
		}
	}

	handleInnerGridVisibility(index: number)
	{
		let isVisible = this.dataSet[index].isInnerTableVisible ?? false;
		this.dataSet[index].isInnerTableVisible = !isVisible;
	}

	// emulate the click in row not already in "newRow" or "editing"
	editAll()
	{
		const dataSet = this.dataSet;
		for (let i = 0; i < dataSet.length; i++)
		{
			if (!dataSet[i].newRow && !dataSet[i].editing)
			{
				this.editRow(i);
			}
		}
	}

	// emulate the click on cancel button for rows in state "newRow" or "editing"
	exitAllRow()
	{
		const dataSet = this.dataSet;
		for (let i = 0; i < dataSet.length; i++)
		{
			if (dataSet[i].newRow || dataSet[i].editing)
			{
				this.exitRow(i);
			}
		}
	}

	// ITOEM-5 added restart table action
	reloadRow(index: number, rollback: boolean = false)
	{
		this.rowNumber = index;
		return this.dataSet;
	}

	SortingUserPref(page_id: string, operation: string) : void
	{
		if ( operation === 'init' )
		{
			// initialize sorting data from localStorage
			this.sortedColumns = this.sortService.loadPageSortingPresets(page_id, this.sortedColumns);
		}
		else if ( operation === 'update' )
		{
			// update localStorage with sorting data from memory

			if ( this.sortedColumns && this.sortedColumns.size > 0 )
			{
				// there is some sorting data to store
				this.sortService.storePageSortingPreset(page_id, this.sortedColumns);
			}
		}
		else if ( operation === 'reset' )
			{
				// remove sorting data from localStorage
				this.sortService.deletePageSortingPresets(page_id);
			}
	}
}
